import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDataFetchingClient } from 'data-fetching-client';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { getParentFolderId } from 'framework-listing-lib/internal/Folders/utils/folders';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useHasNestedFolders } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useFetchFolderPath from './useFetchFolderPath';
import { updateFoldersCache } from '../utils/cache';
import useFetchFolders from './useFetchFolders';
export default function useFetchFolderHierarchy(currentFolderId) {
  const hasNestedFolders = useHasNestedFolders();
  const client = useDataFetchingClient();
  const objectType = useObjectTypeId();

  /**
   * Ref to call the paths endpoint only once.
   * Has a truthy value when rendering the UI for the first time from the top-level (not inside of any folder).
   */
  const skipFetchFolderPath = useRef(currentFolderId === -1 || !hasNestedFolders);

  /**
   * Map of all current open folders in the UI.
   * @key represents a parent folder id and its @value is a list of all opened child folder ids.
   *
   * {
   *   [-1]: [Folder 1, Folder 2],  // Two folders open in the first level
   *   [Folder 1]: [sub folder 1.1],
   *   [Folder 2]: [sub folder 2.1, sub folder 2.2]
   * }
   *
   * When a folder is closed, its id is removed from the list.
   * We use this map to find which folders are open and which folder are not open.
   * Open folders trigger an API call to fetch the child folders.
   */
  const [openFoldersMap, setOpenFoldersMap] = useState({});
  const {
    folders: folderHierarchy,
    loading: loadingHierarchy
  } = useFetchFolderPath({
    currentFolderId,
    skip: skipFetchFolderPath.current
  });

  /**
   * List of all folder ids we need to fetch in order to rebuild
   * the folder tree navigation.
   * Only necessary when loading the UI for the first time inside of a folder.
   */
  const folderIdsWithMissingChildFolders = useMemo(() => {
    if (skipFetchFolderPath.current || loadingHierarchy) {
      return [];
    }
    return [currentFolderId, ...folderHierarchy.map(getParentFolderId).filter(parentFolderId => parentFolderId !== -1)];
  }, [currentFolderId, folderHierarchy, loadingHierarchy]);
  const skipFetchFolders = useMemo(() => loadingHierarchy || folderHierarchy.length === 0 || folderIdsWithMissingChildFolders.length === 0, [folderHierarchy.length, folderIdsWithMissingChildFolders.length, loadingHierarchy]);
  const {
    folders,
    loading: loadingFolders
  } = useFetchFolders({
    objectType,
    parentFolderId: folderIdsWithMissingChildFolders,
    skip: skipFetchFolders
  });
  const isLoading = useMemo(() => loadingHierarchy || loadingFolders, [loadingFolders, loadingHierarchy]);
  const onClickFolder = useCallback(folder => {
    const parentFolderId = getParentFolderId(folder);
    setOpenFoldersMap(prevMap => {
      // Closing or re-selecting folder
      if (parentFolderId in prevMap && prevMap[parentFolderId].includes(folder.objectId)) {
        // re-selecting folder
        if (currentFolderId !== folder.objectId) {
          return prevMap;
        }
        return Object.assign({}, prevMap, {
          [parentFolderId]: prevMap[parentFolderId].filter(objectId => objectId !== folder.objectId),
          [folder.objectId]: []
        });
      }

      // opening folder
      return Object.assign({}, prevMap, {
        [parentFolderId]: parentFolderId in prevMap ? [...prevMap[parentFolderId], folder.objectId] : [folder.objectId]
      });
    });
  }, [currentFolderId]);
  useEffect(() => {
    if (isLoading || folderHierarchy.length === 0 || skipFetchFolderPath.current) {
      return;
    }
    skipFetchFolderPath.current = true;

    /**
     * For every folder in @folderHierarchy:
     *  - Set its parent folder as open for the folder tree navigation (by updating @setOpenFoldersMap)
     *  - Update folders cache for that folder with its child folders (result from useFetchFolders)
     */
    for (const crmFolder of folderHierarchy) {
      const parentFolderId = getParentFolderId(crmFolder);

      // Mark folder as selected when fetching from hierarchy
      if (crmFolder.objectId === currentFolderId) {
        listingLibObserver.folderSelected(crmFolder);
      }

      // Find child folders of @crmFolder and update cache
      const childFolders = folders.filter(crmObject => {
        const folderParentId = getParentFolderId(crmObject);
        return folderParentId === crmFolder.objectId;
      });
      setOpenFoldersMap(prevMap => Object.assign({}, prevMap, {
        [parentFolderId]: [crmFolder.objectId]
      }));
      updateFoldersCache(client.cache, {
        objectType,
        parentFolderId: crmFolder.objectId
      }, childFolders);
    }

    /**
     * Scroll to current folder
     * We don't need a cleanup in the this setTimeout since we're not mutating
     * anything that is directly affecting the lifecycle of this component.
     */
    setTimeout(() => {
      if (document.querySelector('[data-test-id="crm-listing-move-assets-modal"]')) {
        return;
      }
      const folderNavItemElement = document.querySelector(`[data-test-id="crm-listing-folder-nav"][data-folder-id="${currentFolderId}"]`);
      if (!folderNavItemElement) {
        return;
      }
      folderNavItemElement.scrollIntoView({
        behavior: 'smooth'
      });
    }, 500);
  }, [client.cache, currentFolderId, folderHierarchy, folders, isLoading, objectType]);
  return {
    openFoldersMap,
    onClickFolder
  };
}